<template>
  <v-row class="mt-2 pl-5 pr-5 d-flex justify-center">

    <v-dialog
      v-model="deploying"
      hide-overlay
      persistent
      width="400"
    >
      <v-card        
        dark
      >        
        <v-card-text class="ma-2">
          <div class="text-h6 align-center pa-4">
            Setting things up, please wait...
          </div>

          <v-alert v-if="error" dense outlined type="error">
            Oops, failed to set it up for your, error: <strong>{{ error }}</strong>.
            <br/>            
            <br/>            
            Please contact <a href="mailto:hello@synpse.net">hello@synpse.net</a> for assistance.
          </v-alert>
          
          <v-progress-linear
            v-else
            indeterminate
            color="white"
            class=""
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-col class="" md="8" sm="12" xs="12">
      <v-stepper v-model="e1" style="width: 100%;">
        <v-stepper-header>
          <v-stepper-step :complete="e1 > 1" step="1">
            Devices
          </v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step :complete="e1 > 2" step="2">
            Applications
          </v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step :complete="e1 > 3" step="3">
            Register a device
          </v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step step="4">
            Deploy application
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <!-- What are devices -->
          <v-stepper-content step="1">
            <v-card class="mb-12 mx-auto" color="black">
              <div class="d-flex flex-no-wrap justify-center">
                <div>
                  <v-card-title class="text-h5 mb-5">
                    Understanding Devices
                  </v-card-title>

                  <v-card-subtitle style="width: 350px;">
                    Devices refer to both tangible hardware (such as laptops, Raspberry Pi units, Nvidia GPU nodes) and
                    virtual machines (AWS, GCP, Azure) linked to our platform.
                    <br><br>
                    Once linked, you can remotely access them (SSH), share with your team and deploy applications.
                  </v-card-subtitle>
                </div>
                <div>
                  <v-avatar class="ma-3" size="300" tile>
                    <img alt="Synpse nodes" height="300px" src="/nodes.webp" />
                  </v-avatar>
                </div>
              </div>
            </v-card>

            <div class="d-flex justify-end">


              <v-btn small color="primary" outlined @click="e1 = 2">
                Continue
              </v-btn>
            </div>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-card class="mb-12 mx-auto" color="black">
              <div class="d-flex flex-no-wrap justify-center">
                <div>
                  <v-card-title class="text-h5 mb-5">
                    Understanding Applications
                  </v-card-title>

                  <v-card-subtitle style="width: 350px;">
                    Applications encompass the range of software deployable on your devices, from straightforward tools
                    like
                    Python apps and JupyterLab to intricate solutions including web applications and advanced LLM models
                    (e.g., Ollama, vLLM).
                    <br><br>
                    Customize application templates to dynamically incorporate environment variables from specific
                    devices,
                    enhancing flexibility and customization
                  </v-card-subtitle>
                </div>
                <div>
                  <v-avatar class="ma-3" size="300" tile>
                    <img alt="Synpse nodes" height="300px" src="/apps_v2.webp" />
                  </v-avatar>
                </div>
              </div>
            </v-card>

            <div class="d-flex justify-end">
              <v-btn small text class="mr-2" @click="e1 = e1 - 1">
                Back
              </v-btn>

              <v-btn small color="primary" :disabled="!registrationToken" outlined @click="e1 = 3">
                Continue
              </v-btn>
            </div>
          </v-stepper-content>

          <!-- Connecting device -->
          <v-stepper-content step="3">
            <v-card class="mb-12 mx-auto" color="black">
              <div class="d-flex flex-no-wrap justify-center">
                <div>
                  <v-card-title class="text-h5 mb-5">
                    Register your device
                  </v-card-title>

                  <v-card-subtitle style="width: 600px;">
                    To start using your Linux or MacOS machine, run this
                    command on one or more devices:

                    <markup class="mt-5" language="bash" :code="installCommand" :inline="false"></markup>

                    <!-- <v-divider class="mt-5"></v-divider> -->

                    <div v-if="devices.length === 0" class="mt-5">
                      <v-progress-circular indeterminate class="mr-2" :width="2" color="primary"></v-progress-circular>
                      Waiting for device...
                    </div>

                    <div v-else-if="devices.length === 1" class="mt-5">
                      <v-icon dark color="green" class="mr-2">
                        check
                      </v-icon>
                      Device registered!
                    </div>

                    <div v-else-if="devices.length > 1" class="mt-5">
                      <v-icon dark color="green" class="mr-2">
                        check
                      </v-icon>
                      {{ devices.length }} devices registered!
                    </div>


                  </v-card-subtitle>
                </div>
                <div>
                  <v-avatar class="ma-3" size="300" tile>
                    <img alt="Synpse nodes" height="300px" src="/new_node.webp" />
                  </v-avatar>
                </div>
              </div>
            </v-card>

            <div class="d-flex justify-end">
              <v-btn small text class="mr-2" @click="e1 = e1 - 1">
                Back
              </v-btn>

              <v-btn :disabled="devices.length === 0" small color="primary" outlined @click="e1 = 4">
                Continue
              </v-btn>
            </div>
          </v-stepper-content>

          <v-stepper-content step="4">
            <v-card class="mb-12 mx-auto" color="black">
              <div class="d-flex flex-no-wrap justify-center">
                <div>
                  <v-card-title class="text-h5 mb-5">
                    Deploying applications
                  </v-card-title>

                  <v-card-subtitle>
                    Choose from one of the templates and deploy it:
                  </v-card-subtitle>

                  <!-- <v-row class="mt-4" justify="space-around"> -->
                  <v-row class="ma-0" dense>

                    <v-col v-for="(item, i) in apps" :key="i" cols="12">
                      <v-card outlined class="mb-1 ml-2 mr-2" color="">
                        <div class="d-flex flex-no-wrap justify-space-between">
                          <div>
                            <v-card-title class="text-subtitle-1 mb-1" v-text="item.name"></v-card-title>

                            <v-card-subtitle class="text-body-2" v-text="item.description"></v-card-subtitle>

                            <v-card-actions>
                                <v-btn
                                  class="ml-2 mt-1 primary--text" 
                                  outlined 
                                  small 
                                  @click="deployApplication(item)"
                                  :disabled="deploying || devices.length === 0"
                                >
                                  Deploy
                                </v-btn>                              
                            </v-card-actions>
                          </div>

                          <v-avatar class="ma-3" size="80" tile :color="item.bg">
                            <v-img :src="item.src"></v-img>
                          </v-avatar>
                        </div>
                      </v-card>
                    </v-col>

                    <v-col cols="12">
                      <v-card outlined class="mb-1 ml-2 mr-2" color="">
                        <div class="d-flex flex-no-wrap justify-space-between">
                          <div>
                            <v-card-title class="text-subtitle-1">Gallery</v-card-title>

                            <v-card-subtitle class="text-body-2">
                              Alternatively, choose an application from our templates gallery that
                              contains one-click deployments for various ML/AI tools, databases, IDEs, etc.                            
                            </v-card-subtitle>

                            <v-card-actions>
                                <v-btn
                                  class="ml-2 mt-1 primary--text" 
                                  outlined 
                                  small 
                                  router
                                  to="/templates"
                                  :disabled="deploying || devices.length === 0"
                                >
                                  View templates
                                </v-btn>                              
                            </v-card-actions>
                          </div>
                          
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-card>

            <div class="d-flex justify-end">
              <v-btn small text class="mr-2" @click="e1 = e1 - 1">
                Back
              </v-btn>
            </div>
          </v-stepper-content>          
        </v-stepper-items>
      </v-stepper>
    </v-col>
  </v-row>
</template>

<style>
.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@-moz-keyframes loader {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}

@-webkit-keyframes loader {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}

@-o-keyframes loader {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes loader {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>

<script>
import YAML from 'yaml';

import Markup from '../helpers/Markup';

export default {
  components: {
    Markup
  },

  data() {
    return {
      e1: 1,
      timer: null,
      deploying: false,
      apps: [
        {
          name: 'JupyterLab',
          description: 'JupyterLab is a web-based interactive development environment for Jupyter notebooks, code, and data. Remotely access and work on your server.',
          src: '/jupyter.svg',
          templateUrl: 'https://raw.githubusercontent.com/synpse-hq/synpse/main/samples/jupyterlab/jupyterlab.yaml',
        },
        {
          name: 'Ollama',
          description: 'Ollama is a large language model (LLM) server that allows you to run many different kinds of models. Exposes port 11434 and mounts /data/ollama for caching.',
          src: '/ollama-2.png',
          bg: 'white',
          templateUrl: 'https://raw.githubusercontent.com/synpse-hq/synpse/main/samples/ollama/ollama.yaml',
        },
        {
          name: 'Nginx',
          description: 'Example nginx "hello world" web server. Demonstrates how to deploy a simple container with an exposed HTTP port.',
          src: '/nginx.jpg',
          templateUrl: 'https://raw.githubusercontent.com/synpse-hq/synpse/main/samples/nginx/nginx.yaml',
        },
      ]
    }
  },

  beforeDestroy() {
    clearInterval(this.timer)
  },

  beforeUnmount() {
    clearInterval(this.timer)
  },

  mounted() {
    this.$store.dispatch('GetProjectMemberships')
  },

  watch: {
    // Once project is loaded, load registration tokens
    project() {
      let q = {
        projectId: this.project.id
      }
      this.$store.dispatch('ListDeviceRegistrationTokens', q)

      // Start polling devices every 2 seconds
      this.timer = setInterval(() => {
        this.fetchDevices()
      }, 2000)
    }
  },

  computed: {
    projectMemberships() {
      return this.$store.state.project.memberships
    },
    project() {
      if (this.$store.state.project.memberships.length === 0) return {}
      // Return the first one
      return this.$store.state.project.memberships[0].project
    },
    registrationToken() {
      if (this.$store.state.device.registrationTokens.length === 0) return {}
      return this.$store.state.device.registrationTokens[0]
    },
    namespaces() {
      return this.$store.state.namespace.namespaces
    },
    devices() {
      return this.$store.state.device.devices
    },
    installCommand() {
      // some crappy formatting
      return `curl https://downloads.synpse.net/install.sh | \\\n  AGENT_PROJECT=${this.project.id} \\\n  AGENT_REGISTRATION_TOKEN=${this.registrationToken?.id} \\\n  AGENT_CONTROLLER_URI=${window.location.protocol}//${window.location.host}/api \\\n  bash`
    },
    loading() {
      return this.$store.state.application.loading
    },
    error() {
      return this.$store.state.application.error
    },
    applicationError() {
      return this.$store.state.application.error
    },
    applications() {
      return this.$store.state.application.applications
    },
    applicationTemplate() {
      return this.$store.state.application.deployTemplate
    },
    createdApplication() {
      return this.$store.state.application.createdApplication
    },
  },

  methods: {
    async fetchDevices() {
      let q = {
        projectId: this.project.id,
        query: '',
        pageSize: 10,
        pageToken: '',
      }
      await this.$store.dispatch('GetDevices', q)
    },
    async deployApplication(app) {
      // Start progress
      this.deploying = true
      
      // Loading namespaces
      let q = {
        project: this.project.id
      }
      await this.$store.dispatch('ListNamespaces', q)

      if (this.namespaces.length === 0) {
        this.error = 'No namespaces found'
        return
      }

      // Get applications to check if the name is already taken
      let appQuery = {
        projectId: this.project.id,
        namespaceId: this.namespaces[0].id
      }
      this.$store.dispatch('ListApplications', appQuery)

      await this.$store.dispatch('GetApplicationDeploymentTemplate', {
        fileUrl: app.templateUrl,
      })

      if (this.applicationError) {
        this.error = this.applicationError
        return
      }

      let application = {}

      try {
        application = YAML.parse(this.applicationTemplate)
      }
      catch (err) {
        this.yamlError = err
        return
      }

      application.scheduling.devices = this.devices.map(d => d.id)

      application.name = this.getApplicationName(application)
      application.projectId = this.project.id
      application.namespaceId = this.namespaces[0].id

      this.$store.dispatch('CreateApplication', application).then(() => {
        if (this.error === null) {
          this.deploying = false
          
          // All good
          this.$store.dispatch('Notify', `Application created`)
          // Change route here to the newly created application
          this.$router.push(
            {
              name: 'applicationDetails',
              params: { 
                project: application.projectId,
                namespace: application.namespaceId,
                application: this.createdApplication.id,
              }
            })

          return
        }
        // Showing error
      })
    },
    getApplicationName(app) {
      // If there's already an application with this name, append 4 random characters
      if (this.applications.find(a => a.name === app.name)) {
        return app.name + '-' + Math.random().toString(36).substring(7)
      }

      return app.name
    },
    finish() {
      this.$router.push({ name: 'devices', params: { project: this.project.id } })
    }
  }
}
</script>